import { getComponentIcon } from '@/utils';
import {
  Component,
  CustomComponentManager,
  RuntimeComponent,
  SelectManager,
} from '@easytwin/runtime';
import { message, Modal, Tooltip, Trigger } from '@easyv/react-components';
import { ArrowBigUpOutlined, ClapperBoardOutlined } from '@easyv/react-icons';
import classNames from 'classnames';
import * as React from 'react';
import { useRequest } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { PreviewVideoCard } from '../PreviewVideoCard';
import Styles from './index.module.less';
import { HintTooltip } from '../HintTooltip';

type PreviewProps = {
  url: string;
};

interface ComponentBasePanelProps {
  comp: RuntimeComponent;
  previewProps?: PreviewProps;
  titleExtra?: React.ReactNode;
}

export const ComponentBasePanel: React.FC<React.PropsWithChildren<ComponentBasePanelProps>> =
  observer((props) => {
    const { comp, children, titleExtra, previewProps } = props;
    console.log(`${comp.type}组件:${comp.version}`);
    const { data: isNeedUpdating } = useRequest(
      async () => {
        if (!(comp instanceof Component)) {
          return false;
        }

        const customComponentManager = comp.engine.getManager(CustomComponentManager);
        const isStale = await customComponentManager.isComponentStale(comp);
        return isStale;
      },
      {
        refreshDeps: [comp, comp.version],
      },
    );

    const updateModule = () => {
      Modal.confirm({
        title: '更新模块',
        content: '更新后有一定可能不兼容导致功能不用，如有重要数据请备份场景或拷贝对应内容后更新',
        okButtonProps: { size: 'small' },
        cancelButtonProps: { size: 'small' },
        async onOk() {
          try {
            if (!(comp instanceof Component)) return;
            const scene = comp.scene;
            const customComponentManager = comp.engine.getManager(CustomComponentManager);
            const selectMgr = scene.getManager(SelectManager);

            const latest = await customComponentManager.getComponentLastVersion(comp.type);
            await scene.updateComponentVersion([{ comp, version: latest }]);
            const newVersionComp = scene.rootComponent?.findBy((a) => a.id === comp.id);
            selectMgr?.unSelectAll();
            if (!newVersionComp) {
              console.error('本次模块升级没有创建新组件');
              return;
            }
            const module = customComponentManager.get(newVersionComp.type, newVersionComp.version);
            if (!module) {
              console.warn('模块升级: 找不到新版组件的module');
              return;
            }
            if (module.Settings.category === 'CustomComponent') {
              newVersionComp && selectMgr?.select([newVersionComp], false);
            }
            message.success(`${comp.name}已更新`);
          } catch (err) {
            console.trace((err as Error).message);
            message.error((err as Error).message);
          }
        },
      });
    };

    return (
      <div className='h-full flex flex-col'>
        <div
          className={classNames(
            'px-3 text-white flex items-center justify-between border-b-[1px] border-FFFFFF0F',
            previewProps && Styles['component-panel-header'],
          )}
          style={{ height: 42 }}
        >
          <div className='flex item-center gap-x-2 text-sm overflow-hidden'>
            <span className='flex items-center'>{getComponentIcon(comp)}</span>
            {previewProps ? (
              <Trigger
                duration={0}
                popup={() => (
                  <PreviewVideoCard
                    url={previewProps.url}
                    loop={false}
                    highlightDirection='right'
                  />
                )}
                position='left'
                popupAlign={{ left: 20 }}
              >
                <div className='overflow-hidden whitespace-nowrap text-ellipsis'>
                  <HintTooltip content={comp.name}>
                    <span className='truncate'>
                      <span>{comp.name}</span>
                    </span>
                  </HintTooltip>
                  <ClapperBoardOutlined
                    size={8}
                    className='relative text-content3 bottom-2 group-hover:text-white'
                  />
                </div>
              </Trigger>
            ) : (
              <HintTooltip content={comp.name}>
                <div className='flex-1 truncate'>
                  <span>{comp.name}</span>
                </div>
              </HintTooltip>
            )}
            <span className='text-content3 text-xs leading-5'>v{comp.version}</span>
          </div>
          <div className='flex items-center'>
            {isNeedUpdating && (
              <Tooltip content='更新'>
                <div
                  className='w-6 h-6 text-primary cursor-pointer hover:bg-FFFFFF0A flex items-center justify-center rounded'
                  onClick={async (e) => {
                    e.stopPropagation();
                    updateModule();
                  }}
                >
                  <ArrowBigUpOutlined size={14} />
                </div>
              </Tooltip>
            )}
            {titleExtra}
          </div>
        </div>
        <div className={classNames('flex-auto overflow-y-auto no-scroll-container h-0 pb-12')}>
          <div>{children}</div>
        </div>
      </div>
    );
  });
